/**
 * รูปแบบ error เดียวกันทั้งระบบ
 *   { "error": { "code", "message", "details"?, "requestId" } }
 *
 * message เป็นภาษาไทยเพราะแสดงตรงให้ผู้ใช้เห็น
 * code เป็นค่าคงที่สำหรับให้ client แยกกรณีและสำหรับ log/alert
 */

export const ERROR_CODES = {
  // 400
  VALIDATION_FAILED: 'VALIDATION_FAILED',
  REGISTRATION_INVALID: 'REGISTRATION_INVALID',
  GPS_ACCURACY_NOT_MET: 'GPS_ACCURACY_NOT_MET',
  GPS_POSITION_STALE: 'GPS_POSITION_STALE',
  IMAGE_TOO_LARGE: 'IMAGE_TOO_LARGE',
  IMAGE_TYPE_NOT_ALLOWED: 'IMAGE_TYPE_NOT_ALLOWED',
  IMAGE_CHECKSUM_MISMATCH: 'IMAGE_CHECKSUM_MISMATCH',
  IMAGE_MISSING: 'IMAGE_MISSING',
  INVALID_STATUS_TRANSITION: 'INVALID_STATUS_TRANSITION',
  // 401 / 403
  UNAUTHENTICATED: 'UNAUTHENTICATED',
  FORBIDDEN: 'FORBIDDEN',
  USER_INACTIVE: 'USER_INACTIVE',
  AREA_FORBIDDEN: 'AREA_FORBIDDEN',
  EDIT_WINDOW_EXPIRED: 'EDIT_WINDOW_EXPIRED',
  // 404
  NOT_FOUND: 'NOT_FOUND',
  // 409
  DUPLICATE_REGISTRATION: 'DUPLICATE_REGISTRATION',
  IDEMPOTENCY_KEY_REUSED: 'IDEMPOTENCY_KEY_REUSED',
  VERSION_CONFLICT: 'VERSION_CONFLICT',
  // 410
  UPLOAD_AUTHORIZATION_EXPIRED: 'UPLOAD_AUTHORIZATION_EXPIRED',
  // 413 / 415
  PAYLOAD_TOO_LARGE: 'PAYLOAD_TOO_LARGE',
  UNSUPPORTED_MEDIA_TYPE: 'UNSUPPORTED_MEDIA_TYPE',
  // 429
  RATE_LIMITED: 'RATE_LIMITED',
  // 500 / 503
  INTERNAL_ERROR: 'INTERNAL_ERROR',
  STORAGE_ERROR: 'STORAGE_ERROR',
  DATABASE_ERROR: 'DATABASE_ERROR',
} as const;

export type ErrorCode = (typeof ERROR_CODES)[keyof typeof ERROR_CODES];

export const ERROR_MESSAGES_TH: Record<ErrorCode, string> = {
  VALIDATION_FAILED: 'ข้อมูลที่ส่งมาไม่ถูกต้อง กรุณาตรวจสอบแล้วลองใหม่',
  REGISTRATION_INVALID: 'เลขทะเบียนลูกค้าไม่ตรงรูปแบบที่กำหนด',
  GPS_ACCURACY_NOT_MET: 'ความแม่นยำของพิกัดยังไม่ผ่านเกณฑ์ที่กำหนด',
  GPS_POSITION_STALE: 'พิกัดเก่าเกินไป กรุณาวัดตำแหน่งใหม่ก่อนบันทึก',
  IMAGE_TOO_LARGE: 'ไฟล์รูปใหญ่เกินขนาดที่อนุญาต',
  IMAGE_TYPE_NOT_ALLOWED: 'ชนิดไฟล์รูปนี้ไม่รองรับ',
  IMAGE_CHECKSUM_MISMATCH: 'ไฟล์รูปที่อัปโหลดไม่ตรงกับค่าตรวจสอบ กรุณาอัปโหลดใหม่',
  IMAGE_MISSING: 'ยังไม่พบไฟล์รูปในระบบจัดเก็บ กรุณาอัปโหลดก่อนยืนยัน',
  INVALID_STATUS_TRANSITION: 'ไม่สามารถเปลี่ยนสถานะรายการจากสถานะปัจจุบันได้',
  UNAUTHENTICATED: 'กรุณาเข้าสู่ระบบก่อนใช้งาน',
  FORBIDDEN: 'บัญชีของคุณไม่มีสิทธิ์ดำเนินการนี้',
  USER_INACTIVE: 'บัญชีนี้ถูกระงับการใช้งาน กรุณาติดต่อผู้ดูแลระบบ',
  AREA_FORBIDDEN: 'คุณไม่มีสิทธิ์เข้าถึงข้อมูลของพื้นที่นี้',
  EDIT_WINDOW_EXPIRED: 'พ้นกำหนดเวลาที่แก้ไขรายการนี้ได้แล้ว กรุณาแจ้งหัวหน้างาน',
  NOT_FOUND: 'ไม่พบข้อมูลที่ต้องการ',
  DUPLICATE_REGISTRATION: 'เลขทะเบียนนี้มีรายการที่ยังใช้งานอยู่แล้ว',
  IDEMPOTENCY_KEY_REUSED: 'คำขอนี้ถูกใช้กับข้อมูลชุดอื่นไปแล้ว',
  VERSION_CONFLICT: 'มีผู้อื่นแก้ไขรายการนี้ไปแล้ว กรุณาโหลดข้อมูลใหม่',
  UPLOAD_AUTHORIZATION_EXPIRED: 'สิทธิ์อัปโหลดหมดอายุ กรุณาขอใหม่แล้วลองอีกครั้ง',
  PAYLOAD_TOO_LARGE: 'ข้อมูลที่ส่งมามีขนาดใหญ่เกินไป',
  UNSUPPORTED_MEDIA_TYPE: 'ระบบไม่รองรับชนิดข้อมูลนี้',
  RATE_LIMITED: 'ส่งคำขอถี่เกินไป กรุณารอสักครู่แล้วลองใหม่',
  INTERNAL_ERROR: 'ระบบขัดข้อง กรุณาลองใหม่อีกครั้ง',
  STORAGE_ERROR: 'ระบบจัดเก็บไฟล์ขัดข้อง กรุณาลองใหม่อีกครั้ง',
  DATABASE_ERROR: 'ระบบฐานข้อมูลขัดข้อง กรุณาลองใหม่อีกครั้ง',
};

export const ERROR_STATUS: Record<ErrorCode, number> = {
  VALIDATION_FAILED: 400,
  REGISTRATION_INVALID: 400,
  GPS_ACCURACY_NOT_MET: 400,
  GPS_POSITION_STALE: 400,
  IMAGE_TOO_LARGE: 400,
  IMAGE_TYPE_NOT_ALLOWED: 400,
  IMAGE_CHECKSUM_MISMATCH: 400,
  IMAGE_MISSING: 400,
  INVALID_STATUS_TRANSITION: 400,
  UNAUTHENTICATED: 401,
  FORBIDDEN: 403,
  USER_INACTIVE: 403,
  AREA_FORBIDDEN: 403,
  EDIT_WINDOW_EXPIRED: 403,
  NOT_FOUND: 404,
  DUPLICATE_REGISTRATION: 409,
  IDEMPOTENCY_KEY_REUSED: 409,
  VERSION_CONFLICT: 409,
  UPLOAD_AUTHORIZATION_EXPIRED: 410,
  PAYLOAD_TOO_LARGE: 413,
  UNSUPPORTED_MEDIA_TYPE: 415,
  RATE_LIMITED: 429,
  INTERNAL_ERROR: 500,
  STORAGE_ERROR: 502,
  DATABASE_ERROR: 503,
};

/** error ที่ client ควรลองใหม่ได้ด้วย exponential backoff */
export const RETRYABLE_ERROR_CODES: readonly ErrorCode[] = [
  ERROR_CODES.RATE_LIMITED,
  ERROR_CODES.INTERNAL_ERROR,
  ERROR_CODES.STORAGE_ERROR,
  ERROR_CODES.DATABASE_ERROR,
];

export interface ApiErrorBody {
  error: {
    code: ErrorCode;
    message: string;
    details?: unknown;
    requestId?: string;
  };
}

export class AppError extends Error {
  readonly code: ErrorCode;
  readonly status: number;
  readonly details?: unknown;
  /** true = ปลอดภัยที่จะแสดง details ให้ผู้ใช้เห็น (ไม่มีข้อมูลภายในระบบ) */
  readonly exposeDetails: boolean;

  constructor(
    code: ErrorCode,
    options: { message?: string; details?: unknown; exposeDetails?: boolean } = {},
  ) {
    super(options.message ?? ERROR_MESSAGES_TH[code]);
    this.name = 'AppError';
    this.code = code;
    this.status = ERROR_STATUS[code];
    this.details = options.details;
    this.exposeDetails = options.exposeDetails ?? true;
  }

  toBody(requestId?: string): ApiErrorBody {
    return {
      error: {
        code: this.code,
        message: this.message,
        ...(this.exposeDetails && this.details !== undefined ? { details: this.details } : {}),
        ...(requestId ? { requestId } : {}),
      },
    };
  }
}

export function isRetryableCode(code: string): boolean {
  return (RETRYABLE_ERROR_CODES as readonly string[]).includes(code);
}
